import { useEffect, useState } from "react";
import { MapPin, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";

const RepAreas = ({ rep, onSave }) => {
  const [text, setText] = useState("");
  const add = () => { const v = text.split(",").map((s) => s.trim()).filter(Boolean); if (v.length) { onSave(rep, [...rep.areas, ...v]); setText(""); } };
  return (
    <div className="rounded-xl border border-[#E7E2D6] p-3" data-testid={`area-rep-${rep.id}`}>
      <div className="mb-2 text-sm font-semibold">{rep.name}</div>
      <div className="mb-2 flex flex-wrap gap-1.5">
        {rep.areas.length === 0 && <span className="text-xs text-muted-foreground">No areas yet: gets leads by turn</span>}
        {rep.areas.map((a) => (
          <span key={a} className="flex items-center gap-1 rounded-full bg-[#0F382C]/10 px-2.5 py-1 text-xs font-medium text-[#0F382C]" data-testid={`area-chip-${rep.id}-${a}`}>{a}
            <button onClick={() => onSave(rep, rep.areas.filter((x) => x !== a))} className="rounded-full transition-colors hover:text-rose-600" aria-label={`Remove ${a}`} data-testid={`area-remove-${rep.id}-${a}`}><X className="h-3 w-3" /></button>
          </span>
        ))}
      </div>
      <Input value={text} list="unowned-areas" onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} onBlur={add} placeholder="Type an area or 6-digit PIN, press Enter" className="h-9" data-testid={`area-input-${rep.id}`} />
    </div>
  );
};

export const AreaOwnersDialog = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const load = () => api.get("/leads/areas").then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { if (open) load(); }, [open]);
  const save = async (rep, areas) => {
    try { await api.put(`/leads/areas/${rep.id}`, { areas }); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto" data-testid="area-owners-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Area owners</DialogTitle>
          <DialogDescription>New leads (file import or Google Sheet sync) whose area or PIN matches go straight to that salesperson. If two people share an area, the one with fewer open leads gets it. Everything else is shared out by turn.</DialogDescription>
        </DialogHeader>
        {data?.reps.length === 0 && <div className="text-sm text-muted-foreground" data-testid="area-no-reps">Add salespeople in Team first.</div>}
        <div className="space-y-2">{data?.reps.map((r) => <RepAreas key={r.id} rep={r} onSave={save} />)}</div>
        <datalist id="unowned-areas">{data?.unowned.map((u) => <option key={u.name} value={u.name} />)}</datalist>
        {data?.unowned.length > 0 && (
          <div className="rounded-xl bg-[#FBF9F4] p-3 text-xs" data-testid="area-unowned">
            <div className="mb-1.5 flex items-center gap-1 font-semibold text-muted-foreground"><MapPin className="h-3.5 w-3.5 text-[#D96B43]" />Lead areas with no owner yet</div>
            <div className="flex flex-wrap gap-1.5">{data.unowned.map((u) => <span key={u.name} className="rounded-full border border-[#E7E2D6] bg-white px-2 py-0.5">{u.name} <span className="font-mono text-muted-foreground">{u.count}</span></span>)}</div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
